import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Chip, IconButton, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { FC } from 'react'
import { IgnoredActionType, ModerationEvent } from '../../../../api/models'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { UNKNOWN_NAME } from '../cameraOverviewEnums'
import { formatDocumentedByName, getEngagementReviewUrl } from '../cameraOverviewUtils'

interface BundledEventRowProps {
  event: ModerationEvent
}

export const BundledEventRow: FC<BundledEventRowProps> = ({ event }) => {
  const { toast } = useSnackbar()
  const timestamp = DateTime.fromISO(event.createdAt.toString()).toFormat("MM/dd/yyyy 'at' h:mm a")
  const rawDocumentedBy = 'documentedBy' in event.details ? (event.details.documentedBy ?? UNKNOWN_NAME) : (event.createdBy ?? UNKNOWN_NAME)
  const documentedBy = formatDocumentedByName(rawDocumentedBy)
  const engagementReviewUrl = getEngagementReviewUrl(event)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(engagementReviewUrl ?? '')
    toast('Link copied.', { variant: 'success' })
  }

  return (
    <Box
      data-testid={`bundled-event-row-${event.id}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '2.5rem',
        gap: 2,
        pl: 1,
        pr: 2,
        py: 1,
        backgroundColor: 'var(--bg-page)',
        borderRadius: 'var(--border-radius-l)'
      }}
    >
      <Typography variant="body2" sx={{ fontSize: 'var(--size-2xs)', fontWeight: 'bold', width: '8.25rem', flexShrink: 0 }}>
        {timestamp}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: 'var(--size-2xs)',
          width: '20rem',
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        Duplicate event documented by {documentedBy}
      </Typography>
      {engagementReviewUrl && (
        <Typography
          variant="body2"
          data-testid={`bundled-event-engagement-link-${event.id}`}
          sx={{
            fontSize: 'var(--size-2xs)',
            color: 'var(--blue-500)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {engagementReviewUrl}
        </Typography>
      )}
      {engagementReviewUrl && (
        <IconButton size="small" onClick={handleCopy} data-testid={`bundled-event-copy-link-${event.id}`}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      )}
      <Chip label={IgnoredActionType.Ignored} size="small" sx={{ backgroundColor: 'var(--shape-medium)', flexShrink: 0 }} />
    </Box>
  )
}
